
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/auth';
import { environmentSupabase } from '@/integrations/supabase/environment-client';
import { generateAnonymousId } from '@/utils/anonymousId';
import { useCountry } from '@/contexts/country';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { PlusCircle, MinusCircle, SendIcon } from 'lucide-react';

interface SuggestPollModalProps {
  open: boolean;
  onClose: () => void;
}

const SuggestPollModal: React.FC<SuggestPollModalProps> = ({ open, onClose }) => {
  const { user } = useAuth();
  const { countryCode, countryName } = useCountry();
  const [anonymousId, setAnonymousId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [options, setOptions] = useState(['Agree', 'Disagree']);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countries, setCountries] = useState<{ code: string, name: string }[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(countryCode || null);

  // Get anonymous ID for non-logged-in users
  useEffect(() => {
    if (!user) {
      const getAnonymousId = async () => {
        const id = await generateAnonymousId();
        setAnonymousId(id);
      };
      getAnonymousId();
    }
  }, [user]);

  // Fetch available countries
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const { data, error } = await environmentSupabase
          .from('countries')
          .select('code, name')
          .eq('active', true)
          .order('name');
        
        if (error) throw error;
        setCountries(data || []);
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    };

    fetchCountries();
  }, []);

  const handleAddOption = () => {
    setOptions([...options, '']);
  };

  const handleRemoveOption = (index: number) => {
    const newOptions = [...options];
    newOptions.splice(index, 1);
    setOptions(newOptions);
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error('Please enter a poll title');
      return;
    }

    if (options.length < 2) {
      toast.error('Please add at least two options');
      return;
    }

    if (options.some(option => !option.trim())) {
      toast.error('All options must have text');
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('Submitting poll suggestion with data:', {
        title: title.trim(),
        description: description.trim() || null,
        suggested_options: options.filter(o => o.trim()),
        country_code: selectedCountry,
        user_id: user?.id || null,
        anonymous_id: !user ? anonymousId : null
      });

      // Call the insert_poll_suggestion function using environment-aware client
      const { error } = await environmentSupabase.rpc('insert_poll_suggestion', {
        title: title.trim(),
        description: description.trim() || null,
        suggested_options: options.filter(o => o.trim()),
        country_code: selectedCountry,
        user_id: user?.id || null,
        anonymous_id: !user ? anonymousId : null
      });

      if (error) {
        console.error('Supabase RPC error:', error);
        throw error;
      }

      toast.success('Your poll suggestion has been submitted for review');
      onClose();
      
      // Reset form
      setTitle('');
      setDescription('');
      setOptions(['Agree', 'Disagree']);
    } catch (error) {
      console.error('Error submitting poll suggestion:', error);
      
      // More detailed error handling
      if (error instanceof Error) {
        if (error.message.includes('ERR_NAME_NOT_RESOLVED')) {
          toast.error('Network connection error. Please check your internet connection and try again.');
        } else if (error.message.includes('Function not found')) {
          toast.error('Poll suggestion feature is temporarily unavailable. Please try again later.');
        } else {
          toast.error(`Failed to submit poll suggestion: ${error.message}`);
        }
      } else {
        toast.error('Failed to submit poll suggestion. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Suggest a Poll</DialogTitle>
          <DialogDescription>
            Have an idea for a poll? Submit your suggestion here and our team will review it.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="title">Poll Question</Label>
            <Input
              id="title"
              placeholder="e.g., Do you support the new environmental policy?"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description (Optional)</Label>
            <Textarea
              id="description"
              placeholder="Provide context about this poll question"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="country">Country</Label>
            <Select 
              value={selectedCountry || undefined} 
              onValueChange={(value) => setSelectedCountry(value)}
            >
              <SelectTrigger id="country">
                <SelectValue placeholder="Select a country" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="GL">Global</SelectItem>
                {countries.map((country) => (
                  <SelectItem key={country.code} value={country.code}>
                    {country.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label>Poll Options</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleAddOption}
                disabled={options.length >= 5}
              >
                <PlusCircle className="h-4 w-4 mr-1" />
                Add Option
              </Button>
            </div>
            <div className="space-y-2">
              {options.map((option, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                    className="flex-grow"
                  />
                  {options.length > 2 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveOption(index)}
                    >
                      <MinusCircle className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <div className="flex flex-col xs:flex-row gap-2 w-full justify-between items-center">
            <span className="text-xs text-muted-foreground">
              {user ? `Submitting as ${user.email}` : 'Submitting anonymously'}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={isSubmitting || !title.trim()}
                className="gap-1"
              >
                <SendIcon className="h-4 w-4" />
                Submit Suggestion
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SuggestPollModal;
